import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import inventoryApi from '../../services/inventoryApi';

// Query key factory — consistent keys across all inventory components
export const inventoryKeys = {
  all: (org) => ['inventory', org],
  products: (org) => [...inventoryKeys.all(org), 'products'],
  productCategories: (org) => [...inventoryKeys.all(org), 'productCategories'],
  warehouses: (org) => [...inventoryKeys.all(org), 'warehouses'],
  storageLocations: (org, warehouseId) => [...inventoryKeys.warehouses(org), warehouseId, 'locations'],
  suppliers: (org) => [...inventoryKeys.all(org), 'suppliers'],
  purchaseOrders: (org) => [...inventoryKeys.all(org), 'purchaseOrders'],
  purchaseOrderItems: (org, poId) => [...inventoryKeys.purchaseOrders(org), poId, 'items'],
  salesOrders: (org) => [...inventoryKeys.all(org), 'salesOrders'],
  salesOrderItems: (org, soId) => [...inventoryKeys.salesOrders(org), soId, 'items'],
  returnOrders: (org) => [...inventoryKeys.all(org), 'returnOrders'],
  stockMovements: (org) => [...inventoryKeys.all(org), 'stockMovements'],
  stockReservations: (org) => [...inventoryKeys.all(org), 'stockReservations'],
  stockAlerts: (org) => [...inventoryKeys.all(org), 'stockAlerts'],
  inventoryValuations: (org) => [...inventoryKeys.all(org), 'inventoryValuations'],
  productVariants: (org, productId) => [...inventoryKeys.products(org), productId, 'variants'],
  productPriceLists: (org) => [...inventoryKeys.all(org), 'productPriceLists'],
  productSuppliers: (org) => [...inventoryKeys.all(org), 'productSuppliers'],
  resources: (org) => [...inventoryKeys.all(org), 'resources'],
};

// ======================= PRODUCTS =======================

export function useProducts(organization, filters = {}) {
  return useQuery({
    queryKey: [...inventoryKeys.products(organization), filters],
    queryFn: () => inventoryApi.getProducts(organization, filters),
    enabled: !!organization,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateProduct(organization) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => inventoryApi.createProduct(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.products(organization) }),
  });
}

export function useUpdateProduct(organization) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => inventoryApi.updateProduct(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.products(organization) }),
  });
}

export function useDeleteProduct(organization) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => inventoryApi.deleteProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.products(organization) }),
  });
}

export function useImportProducts(organization) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const results = await Promise.allSettled(
        data.products.map(p =>
          inventoryApi.createProduct({ ...p, organization: data.organization })
        )
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length
      const errors = results
        .filter(r => r.status === 'rejected')
        .map((r, i) => ({ row: i + 1, message: r.reason?.message || r.reason }));
      return { succeeded, total: data.products.length, errors };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.products(organization) }),
  });
}

// ======================= CATEGORIES =======================

export function useProductCategories(organization) {
  return useQuery({
    queryKey: inventoryKeys.productCategories(organization),
    queryFn: () => inventoryApi.getProductCategories(organization),
    enabled: !!organization,
    staleTime: 10 * 60 * 1000,
  });
}

// ======================= WAREHOUSES =======================

export function useWarehouses(organization) {
  return useQuery({
    queryKey: inventoryKeys.warehouses(organization),
    queryFn: () => inventoryApi.getWarehouses(organization),
    enabled: !!organization,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateWarehouse(organization) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => inventoryApi.createWarehouse(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: inventoryKeys.warehouses(organization) }),
  });
}

export function useStorageLocations(organization, warehouseId) {
  return useQuery({
    queryKey: inventoryKeys.storageLocations(organization, warehouseId),
    queryFn: () => inventoryApi.getStorageLocations(warehouseId),
    enabled: !!organization && !!warehouseId,
    staleTime: 5 * 60 * 1000,
  });
}

// ======================= SUPPLIERS =======================

export function useSuppliers(organization) {
  return useQuery({
    queryKey: inventoryKeys.suppliers(organization),
    queryFn: () => inventoryApi.getSuppliers(organization),
    enabled: !!organization,
    staleTime: 5 * 60 * 1000,
  });
}

// ======================= PURCHASE ORDERS =======================

export function usePurchaseOrders(organization, status = null) {
  return useQuery({
    queryKey: [...inventoryKeys.purchaseOrders(organization), status],
    queryFn: () => inventoryApi.getPurchaseOrders(organization, status),
    enabled: !!organization,
    staleTime: 2 * 60 * 1000,
  });
}

export function usePurchaseOrderItems(organization, poId) {
  return useQuery({
    queryKey: inventoryKeys.purchaseOrderItems(organization, poId),
    queryFn: () => inventoryApi.getPurchaseOrderItems(poId),
    enabled: !!organization && !!poId,
  });
}

// ======================= SALES ORDERS =======================

export function useSalesOrders(organization, status = null) {
  return useQuery({
    queryKey: [...inventoryKeys.salesOrders(organization), status],
    queryFn: () => inventoryApi.getSalesOrders(organization, null, status),
    enabled: !!organization,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSalesOrderItems(organization, soId) {
  return useQuery({
    queryKey: inventoryKeys.salesOrderItems(organization, soId),
    queryFn: () => inventoryApi.getSalesOrderItems(soId),
    enabled: !!organization && !!soId,
  });
}

// ======================= RETURN ORDERS =======================

export function useReturnOrders(organization, status = null) {
  return useQuery({
    queryKey: [...inventoryKeys.returnOrders(organization), status],
    queryFn: () => inventoryApi.getReturnOrders(organization, null, status),
    enabled: !!organization,
    staleTime: 2 * 60 * 1000,
  });
}

// ======================= STOCK =======================

export function useStockMovements(organization) {
  return useQuery({
    queryKey: inventoryKeys.stockMovements(organization),
    queryFn: () => inventoryApi.getStockMovements(organization),
    enabled: !!organization,
    staleTime: 2 * 60 * 1000,
  });
}

export function useStockReservations(organization) {
  return useQuery({
    queryKey: inventoryKeys.stockReservations(organization),
    queryFn: () => inventoryApi.getStockReservations(organization),
    enabled: !!organization,
    staleTime: 2 * 60 * 1000,
  });
}

export function useStockAlerts(organization, status = 'active') {
  return useQuery({
    queryKey: [...inventoryKeys.stockAlerts(organization), status],
    queryFn: () => inventoryApi.getStockAlerts(organization, { status }),
    enabled: !!organization,
    staleTime: 1 * 60 * 1000,
  });
}

export function useInventoryValuations(organization) {
  return useQuery({
    queryKey: inventoryKeys.inventoryValuations(organization),
    queryFn: () => inventoryApi.getInventoryValuations(organization),
    enabled: !!organization,
    staleTime: 5 * 60 * 1000,
  });
}

// ======================= PRODUCT RELATIONS =======================

export function useProductVariants(organization, productId) {
  return useQuery({
    queryKey: inventoryKeys.productVariants(organization, productId),
    queryFn: () => inventoryApi.getProductVariants(productId, organization),
    enabled: !!organization && !!productId,
  });
}

export function useProductPriceLists(organization) {
  return useQuery({
    queryKey: inventoryKeys.productPriceLists(organization),
    queryFn: () => inventoryApi.getProductPriceLists(organization),
    enabled: !!organization,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProductSuppliers(organization) {
  return useQuery({
    queryKey: inventoryKeys.productSuppliers(organization),
    queryFn: () => inventoryApi.getProductSuppliers(organization),
    enabled: !!organization,
    staleTime: 5 * 60 * 1000,
  });
}

// ======================= RESOURCES (DRAGGIT RM) =======================

export function useResources(organization, type = null) {
  return useQuery({
    queryKey: [...inventoryKeys.resources(organization), type],
    queryFn: () => inventoryApi.getResources(organization, type),
    enabled: !!organization,
    staleTime: 5 * 60 * 1000,
  });
}
